import DOMPurify from 'dompurify';
import i18n from 'src/i18n';
import {
  boundaries,
  LcaDiagramColumns,
  ProductImpactOutSchema,
} from 'src/Lca/LCADiagram/Diagram/index.types';
import { Node } from 'src/Lca/LCADiagram/Diagram/Node/index.types';
import {
  AUDIENCE_DEFAULTS,
  REPORT_SECTIONS,
  REPORT_TYPE_OPTIONS,
  SECTION_ID_TO_STAGE_LABEL,
  getDefaultReportName,
  getDefaultReportTitle,
  getReportSectionTitle,
} from './config';
import {
  Audience,
  BuilderPreviewData,
  NumberFormat,
  ReportConfig,
  ReportProductData,
  ReportSectionConfig,
  ReportSectionCustomContent,
  ReportSectionImage,
  ReportValidationIssue,
  ReadinessMetric,
  SectionId,
  SectionPreview,
  StageHotspot,
} from './types';
import { LcaDiagramSchema } from '../types';

const STAGE_NAME_MAP: Record<string, string> = {
  [LcaDiagramColumns.rawMaterialsExtractionAndProcessing]:
    'stageNames.rawMaterials',
  [LcaDiagramColumns.transportOfRawMaterials]: 'stageNames.transport',
  [LcaDiagramColumns.productionAndPackaging]: 'stageNames.production',
  [LcaDiagramColumns.distributionAndStorage]: 'stageNames.distribution',
  [LcaDiagramColumns.installation]: 'stageNames.installation',
  [LcaDiagramColumns.useStage]: 'stageNames.useStage',
  [LcaDiagramColumns.endOfLife]: 'stageNames.endOfLife',
};

const STAGE_ORDER = [
  LcaDiagramColumns.rawMaterialsExtractionAndProcessing,
  LcaDiagramColumns.transportOfRawMaterials,
  LcaDiagramColumns.productionAndPackaging,
  LcaDiagramColumns.distributionAndStorage,
  LcaDiagramColumns.installation,
  LcaDiagramColumns.useStage,
  LcaDiagramColumns.endOfLife,
] as const;

const LIFECYCLE_BOUNDARY_LABELS: Record<string, string> = {
  cradle_to_gate: 'lifecycleBoundaries.cradleToGate',
  gate_to_gate: 'lifecycleBoundaries.gateToGate',
  cradle_to_grave: 'lifecycleBoundaries.cradleToGrave',
  gate_to_grave: 'lifecycleBoundaries.gateToGrave',
};

const translateReportBuilder = (
  key: string,
  options?: Record<string, unknown>
) => i18n.t(`lca:reportBuilder.${key}`, options) as string;

export const hasMeaningfulValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const EMPTY_CUSTOM_CONTENT: ReportSectionCustomContent = {
  html: '',
  images: [],
};

export const getLifecycleBoundaryLabel = (
  value: string | null | undefined,
  language?: ReportConfig['language']
) =>
  value && LIFECYCLE_BOUNDARY_LABELS[value]
    ? translateReportBuilder(LIFECYCLE_BOUNDARY_LABELS[value], {
        lng: language,
      })
    : translateReportBuilder('common.notProvided', { lng: language });

export const getStageDisplayName = (
  stageKey: string,
  language?: ReportConfig['language']
) =>
  STAGE_NAME_MAP[stageKey]
    ? translateReportBuilder(STAGE_NAME_MAP[stageKey], { lng: language })
    : stageKey;

export const getOrderedStageEntries = (
  emissionPerStage: ProductImpactOutSchema['emission_per_stage'] | undefined,
  language?: ReportConfig['language']
) =>
  STAGE_ORDER.map((stageKey) => ({
    key: stageKey,
    name: getStageDisplayName(stageKey, language),
    code: SECTION_ID_TO_STAGE_LABEL[stageKey] || stageKey,
    value: emissionPerStage?.[stageKey] || 0,
  })).filter((entry) => entry.value > 0);

export const getWeightedRecycledShare = (
  product: ReportProductData
): number => {
  const weightedTotal = product.materials.reduce(
    (sum, material) =>
      sum + material.percentage * (material.recycled_share_pct || 0),
    0
  );
  const totalShare = product.materials.reduce(
    (sum, material) => sum + material.percentage,
    0
  );

  if (!totalShare) return 0;
  return weightedTotal / totalShare;
};

export const getMaterialProfile = (product: ReportProductData, limit = 5) =>
  [...product.materials]
    .sort((left, right) => right.percentage - left.percentage)
    .slice(0, limit);

export const buildReadinessMetrics = (
  product: ReportProductData,
  previewData: BuilderPreviewData,
  language?: ReportConfig['language']
): ReadinessMetric[] => {
  const metricGroups: Array<{ label: string; values: unknown[] }> = [
    {
      label: translateReportBuilder('readinessMetrics.identityTraceability', {
        lng: language,
      }),
      values: [
        product.name,
        product.description,
        product.manufacturer_name,
        product.gtin_code,
        product.production_country?.name,
        product.cpc_industry?.code,
      ],
    },
    {
      label: translateReportBuilder('readinessMetrics.lcaScopeMethodology', {
        lng: language,
      }),
      values: [
        product.functional_unit,
        product.lifecycle_boundaries,
        product.data_collection_period,
        product.production_process_description,
        product.assumptions_description,
        product.cutoff_rules,
        product.goals?.intended_applications,
        product.goals?.study_reasons,
        product.goals?.target_audience,
      ],
    },
    {
      label: translateReportBuilder('readinessMetrics.circularityMaterials', {
        lng: language,
      }),
      values: [
        product.materials,
        product.recycled_materials_share,
        product.renewable_materials_content,
        product.material_recovery_critical_raw_materials,
        product.recycling_possibilities,
        product.disassembly_instructions,
        product.epr_information_description,
      ],
    },
    {
      label: translateReportBuilder('readinessMetrics.durabilityUsePhase', {
        lng: language,
      }),
      values: [
        product.reference_service_life,
        product.guaranteed_service_life,
        product.operating_life,
        product.mean_time_between_failures,
        product.availability_of_spare_parts,
        product.repair_disassembly_instructions_description,
        product.actual_product_use_information,
        product.resistance_to_stress_or_aging_mechanisms,
      ],
    },
    {
      label: translateReportBuilder('readinessMetrics.impactCoverage', {
        lng: language,
      }),
      values: [previewData.availableImpacts, previewData.selectedImpact],
    },
  ];

  return metricGroups.map((group) => {
    const filled = group.values.filter(hasMeaningfulValue).length;
    const total = group.values.length;
    return {
      label: group.label,
      filled,
      total,
      percentage: total ? (filled / total) * 100 : 0,
    };
  });
};

export const getReadinessHighlights = (
  metrics: ReadinessMetric[],
  language?: ReportConfig['language']
): string[] =>
  metrics
    .filter((metric) => metric.percentage < 70)
    .map((metric) =>
      translateReportBuilder('preview.readinessMetricStatus', {
        lng: language,
        label: metric.label,
        percentage: Math.round(metric.percentage),
        filled: metric.filled,
        total: metric.total,
      })
    );

export const hasMaterialsCircularityData = (product: ReportProductData) =>
  [
    product.materials,
    product.recycled_materials_share,
    product.renewable_materials_content,
    product.material_recovery_critical_raw_materials,
    product.recycling_possibilities,
    product.disassembly_instructions,
    product.epr_information_description,
  ].some(hasMeaningfulValue);

export const hasDurabilityData = (product: ReportProductData) =>
  [
    product.reference_service_life,
    product.guaranteed_service_life,
    product.operating_life,
    product.mean_time_between_failures,
    product.availability_of_spare_parts,
    product.repair_disassembly_instructions_description,
    product.actual_product_use_information,
    product.resistance_to_stress_or_aging_mechanisms,
  ].some(hasMeaningfulValue);

export const hasMethodologyData = (product: ReportProductData) =>
  [
    product.goals?.intended_applications,
    product.goals?.study_reasons,
    product.goals?.target_audience,
    product.goals?.comissioner_name,
    product.regulatory_frameworks,
    product.product_category_rules,
    product.cutoff_rules,
    product.assumptions_description,
    product.production_process_description,
    product.lifecycle_boundaries,
  ].some(hasMeaningfulValue);

export const getSection = (
  config: ReportConfig,
  id: SectionId
): ReportSectionConfig | undefined =>
  config.sections.find((section) => section.id === id);

export const replaceSection = (
  config: ReportConfig,
  id: SectionId,
  patch: Partial<ReportSectionConfig>
): ReportConfig => ({
  ...config,
  sections: config.sections.map((section) =>
    section.id === id ? { ...section, ...patch } : section
  ),
});

export const getSectionCustomContent = (
  section?: ReportSectionConfig
): ReportSectionCustomContent =>
  section?.options.customContent || EMPTY_CUSTOM_CONTENT;

export const getSelectedImpactIds = (
  section: ReportSectionConfig | undefined,
  impacts: ProductImpactOutSchema[]
): number[] => {
  const configuredIds = section?.options.selectedAreaIds;
  const availableIds = new Set(impacts.map((impact) => impact.area.id));

  if (!Array.isArray(configuredIds)) {
    return impacts.map((impact) => impact.area.id);
  }

  return configuredIds.filter((id) => availableIds.has(id));
};

export const getSelectedImpacts = (
  section: ReportSectionConfig | undefined,
  impacts: ProductImpactOutSchema[]
): ProductImpactOutSchema[] => {
  const selectedIds = new Set(getSelectedImpactIds(section, impacts));
  return impacts.filter((impact) => selectedIds.has(impact.area.id));
};

export const updateSectionCustomContent = (
  config: ReportConfig,
  id: SectionId,
  patch: Partial<ReportSectionCustomContent>
): ReportConfig => {
  const currentSection = getSection(config, id);
  const currentCustomContent = getSectionCustomContent(currentSection);

  return replaceSection(config, id, {
    options: {
      ...(currentSection?.options || {}),
      customContent: {
        ...currentCustomContent,
        ...patch,
      },
    },
  });
};

export const sanitizeRichTextHtml = (html: string): string =>
  DOMPurify.sanitize(html || '', {
    ALLOWED_TAGS: [
      'a',
      'b',
      'blockquote',
      'br',
      'div',
      'em',
      'h2',
      'i',
      'li',
      'ol',
      'p',
      'strong',
      'ul',
    ],
    ALLOWED_ATTR: ['href', 'rel', 'target'],
    FORBID_TAGS: ['script', 'style'],
    FORBID_ATTR: ['onload', 'onclick', 'onerror'],
  }).trim();

export const moveImage = (
  images: ReportSectionImage[],
  from: number,
  to: number
): ReportSectionImage[] => {
  if (to < 0 || to >= images.length || from === to) {
    return images;
  }

  const nextImages = [...images];
  const [moved] = nextImages.splice(from, 1);
  nextImages.splice(to, 0, moved);
  return nextImages;
};

export const htmlToPlainText = (html: string): string => {
  const sanitized = sanitizeRichTextHtml(html);
  if (!sanitized) return '';

  if (typeof DOMParser !== 'undefined') {
    const parsed = new DOMParser().parseFromString(sanitized, 'text/html');
    return parsed.body.textContent?.replace(/\s+/g, ' ').trim() || '';
  }

  return sanitized
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const getCustomContentWeight = (
  customContent: ReportSectionCustomContent
): number => {
  const textWeight = htmlToPlainText(customContent.html).length / 1600;
  const imageWeight = customContent.images.reduce((sum, image) => {
    if (image.layout === 'full') return sum + 0.9;
    if (image.layout === 'medium') return sum + 0.55;
    return sum + 0.3;
  }, 0);

  return textWeight + imageWeight;
};

export const applyAudiencePreset = (
  config: ReportConfig,
  audience: Audience
): ReportConfig => {
  const audienceDefaults = AUDIENCE_DEFAULTS[audience];

  return {
    ...config,
    audience,
    templateId: audienceDefaults.templateId,
    layout: {
      ...config.layout,
      density: audienceDefaults.density,
      watermark: {
        ...config.layout.watermark,
        enabled: audienceDefaults.watermarkDefault,
      },
    },
    sections: config.sections.map((section) => {
      const isRequired = audienceDefaults.requiredSections.includes(section.id);
      const isDetailed = audienceDefaults.defaultDetailedSections.includes(
        section.id
      );
      return {
        ...section,
        enabled: isRequired ? true : section.enabled,
        detailLevel: isDetailed ? 'detailed' : section.detailLevel,
      };
    }),
  };
};

const countDiagramNodes = (nodes: Node[] = []): number =>
  nodes.reduce(
    (sum, node) => sum + 1 + countDiagramNodes(node.children || []),
    0
  );

const getTopStageNodeLabels = (nodes: Node[] = [], limit = 3): string[] =>
  nodes
    .map((node) => node.name)
    .filter(Boolean)
    .slice(0, limit);

export type FlowchartStageSummary = {
  key: LcaDiagramColumns;
  code: string;
  name: string;
  group: string | null;
  nodeCount: number;
  topLabels: string[];
  impactValue: number;
};

export const getFlowchartStageSummaries = (
  diagramData: LcaDiagramSchema | null | undefined,
  selectedImpact?: ProductImpactOutSchema | null,
  language?: ReportConfig['language']
): FlowchartStageSummary[] => {
  if (!diagramData) return [];

  const availableStageKeys = new Set(
    boundaries[diagramData.lifecycle_boundaries] || []
  );

  return diagramData.stages
    .filter((stage): stage is typeof stage & { key: LcaDiagramColumns } =>
      availableStageKeys.has(stage.key)
    )
    .sort(
      (left, right) =>
        STAGE_ORDER.indexOf(left.key) - STAGE_ORDER.indexOf(right.key)
    )
    .map((stage) => ({
      key: stage.key,
      code: SECTION_ID_TO_STAGE_LABEL[stage.key] || stage.key,
      name: getStageDisplayName(stage.key, language),
      group: stage.group || null,
      nodeCount: countDiagramNodes(stage.children || []),
      topLabels: getTopStageNodeLabels(stage.children || []),
      impactValue: selectedImpact?.emission_per_stage?.[stage.key] || 0,
    }));
};

export const getEnabledSections = (
  config: ReportConfig
): ReportSectionConfig[] =>
  config.sections.filter((section) => section.enabled);

const getSectionWeight = (sectionId: SectionId): number => {
  switch (sectionId) {
    case 'executive-summary':
      return 1.3;
    case 'materials-circularity':
    case 'durability-end-of-life':
      return 1.35;
    case 'data-quality-readiness':
      return 1.1;
    case 'impact-categories':
      return 1.8;
    case 'results-ghg':
      return 1.5;
    case 'disclaimer-audit-trail':
      return 0.8;
    default:
      return 1;
  }
};

const getDetailMultiplier = (
  detailLevel: ReportSectionConfig['detailLevel']
) => {
  if (detailLevel === 'summary') return 0.7;
  if (detailLevel === 'detailed') return 1.65;
  return 1;
};

const getDensityMultiplier = (density: ReportConfig['layout']['density']) => {
  if (density === 'spacious') return 1.18;
  if (density === 'compact') return 0.82;
  return 1;
};

export const estimatePages = (config: ReportConfig): number => {
  if (config.reportType === 'lca-one-pager') {
    return 1;
  }

  const densityMultiplier = getDensityMultiplier(config.layout.density);
  const sectionsWeight = getEnabledSections(config).reduce(
    (sum, section) =>
      sum +
      getSectionWeight(section.id) * getDetailMultiplier(section.detailLevel) +
      getCustomContentWeight(getSectionCustomContent(section)),
    0
  );

  const base = 2;
  return Math.max(2, Math.round((base + sectionsWeight) * densityMultiplier));
};

export const buildSectionPreviews = (
  config: ReportConfig,
  language?: ReportConfig['language']
): SectionPreview[] => {
  if (config.reportType === 'lca-one-pager') {
    return [
      {
        id: 'executive-summary',
        title: translateReportBuilder('preview.onePagerTitle', {
          lng: language,
        }),
        page: 1,
        detailLevel: 'summary',
      },
    ];
  }

  let pageCounter = 1;
  return getEnabledSections(config).map((section) => {
    const definition = REPORT_SECTIONS.find((item) => item.id === section.id);
    const preview: SectionPreview = {
      id: section.id,
      title: definition
        ? getReportSectionTitle(definition.id, language)
        : section.id,
      page: pageCounter,
      detailLevel: section.detailLevel,
    };

    const sectionPages = Math.max(
      1,
      Math.round(
        getSectionWeight(section.id) *
          getDetailMultiplier(section.detailLevel) +
          getCustomContentWeight(getSectionCustomContent(section))
      )
    );
    pageCounter += sectionPages;
    return preview;
  });
};

export const formatNumber = (
  value: number | null | undefined,
  numberFormat: NumberFormat,
  language?: ReportConfig['language']
): string => {
  if (value === null || value === undefined || Number.isNaN(value))
    return translateReportBuilder('common.notProvided', { lng: language });

  const parts = value
    .toFixed(numberFormat.decimals)
    .split('.')
    .map((part, index) => {
      if (index !== 0) return part;
      return part.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        numberFormat.thousandsSeparator
      );
    });

  return parts.join('.');
};

export const computeHotspots = (
  selectedImpact: ProductImpactOutSchema | null
): StageHotspot[] => {
  if (!selectedImpact?.emission_per_stage) return [];

  const entries = Object.entries(selectedImpact.emission_per_stage)
    .map(([stageKey, value]) => ({
      stageKey,
      value: value || 0,
    }))
    .filter((entry) => entry.value > 0);

  const total = entries.reduce((sum, entry) => sum + entry.value, 0);
  if (!total) return [];

  return entries
    .sort((a, b) => b.value - a.value)
    .slice(0, 3)
    .map((entry) => ({
      stageKey: entry.stageKey,
      stageCode: SECTION_ID_TO_STAGE_LABEL[entry.stageKey] || entry.stageKey,
      name: getStageDisplayName(entry.stageKey),
      value: entry.value,
      percentage: (entry.value / total) * 100,
    }));
};

export const buildPreviewData = (
  impacts: ProductImpactOutSchema[],
  selectedAreaId?: number
): BuilderPreviewData => {
  const selectedImpact =
    impacts.find((impact) => impact.area.id === selectedAreaId) ||
    impacts[0] ||
    null;

  return {
    selectedImpact,
    availableImpacts: impacts,
    availableAreas: impacts.map((impact) => impact.area),
    totalFootprint: selectedImpact?.total_emission || null,
    hotspots: computeHotspots(selectedImpact),
    hasScenarioData: false,
  };
};

export const enforceDataDrivenSectionRules = (
  config: ReportConfig,
  previewData: BuilderPreviewData,
  language?: ReportConfig['language']
): ReportConfig => {
  return {
    ...config,
    sections: config.sections.map((section) => {
      if (section.id === 'scenarios' && !previewData.hasScenarioData) {
        return {
          ...section,
          enabled: false,
          options: {
            ...section.options,
            autoDisabledReason: translateReportBuilder(
              'validation.scenariosUnavailable',
              { lng: language }
            ),
          },
        };
      }

      return section;
    }),
  };
};

// Switching the report language leaves the cover title and the report name
// written in the previous one, so the ones nobody edited are re-translated with
// it. The report name is what the saved version is listed under, so a stale one
// keeps the report looking untranslated however English its pages are.
export const applyReportLanguage = (
  config: ReportConfig,
  language: ReportConfig['language'],
  languageSource: ReportConfig['languageSource']
): ReportConfig => {
  if (
    config.language === language &&
    config.languageSource === languageSource
  ) {
    return config;
  }

  const previousDefaultTitles = new Set(
    REPORT_TYPE_OPTIONS.map((option) =>
      getDefaultReportTitle(option.id, config.language)
    )
  );

  // The default report name carries the date it was built on, which no longer
  // exists anywhere else - so the previous default is reconstructed around the
  // date still printed in the name before deciding it was never edited.
  const { reportName } = config.output;
  const dateInReportName = reportName.match(/\d{4}-\d{2}-\d{2}/)?.[0];
  const isDefaultReportName =
    !!dateInReportName &&
    reportName ===
      getDefaultReportName(
        config.branding.productName,
        dateInReportName,
        config.language
      );

  return {
    ...config,
    language,
    languageSource,
    branding: {
      ...config.branding,
      reportTitle: previousDefaultTitles.has(config.branding.reportTitle)
        ? getDefaultReportTitle(config.reportType, language)
        : config.branding.reportTitle,
    },
    output: {
      ...config.output,
      reportName:
        isDefaultReportName && dateInReportName
          ? getDefaultReportName(
              config.branding.productName,
              dateInReportName,
              language
            )
          : reportName,
    },
  };
};

/**
 * A draft remembers the language it was started in, and every draft saved before
 * the language became a deliberate choice carries no languageSource. Honouring
 * those blindly kept generating Polish reports for a session switched to English,
 * so only a language the user picked by hand pins; anything else follows the
 * interface.
 */
export const resolveReportLanguage = (
  config: ReportConfig,
  uiLanguage: ReportConfig['language']
): ReportConfig =>
  config.languageSource === 'manual'
    ? config
    : applyReportLanguage(config, uiLanguage, 'auto');

export const sanitizeOnePagerConfig = (config: ReportConfig): ReportConfig => {
  if (config.reportType !== 'lca-one-pager') {
    return config;
  }

  return {
    ...config,
    branding: {
      ...config.branding,
      coverStyle: 'none',
      coverAssetId: null,
      coverUrl: null,
    },
    output: {
      ...config.output,
      formats: ['pdf'],
    },
  };
};

export const validateReportConfig = (
  config: ReportConfig,
  product: ReportProductData | null,
  previewData: BuilderPreviewData,
  hasWritePermissions: boolean,
  language?: ReportConfig['language']
): ReportValidationIssue[] => {
  const issues: ReportValidationIssue[] = [];
  const isOnePager = config.reportType === 'lca-one-pager';
  const enabledSections = getEnabledSections(config);

  if (!enabledSections.length && !isOnePager) {
    issues.push({
      id: 'NO_SECTIONS_ENABLED',
      blocking: true,
      message: translateReportBuilder('validation.noSectionsEnabled', {
        lng: language,
      }),
    });
  }

  if (!config.output.formats.includes('pdf')) {
    issues.push({
      id: 'PDF_REQUIRED',
      blocking: true,
      message: translateReportBuilder('validation.pdfRequired', {
        lng: language,
      }),
    });
  }

  if (!hasWritePermissions) {
    issues.push({
      id: 'MISSING_PERMISSION',
      blocking: true,
      message: translateReportBuilder('validation.missingPermission', {
        lng: language,
      }),
    });
  }

  if (!config.branding.logoAssetId && !config.branding.logoUrl) {
    issues.push({
      id: 'MISSING_LOGO',
      blocking: false,
      message: translateReportBuilder('validation.missingLogo', {
        lng: language,
      }),
      sectionId: 'producer-info',
    });
  }

  if (
    !isOnePager &&
    config.branding.coverStyle === 'user' &&
    !config.branding.coverAssetId &&
    !config.branding.coverUrl
  ) {
    issues.push({
      id: 'MISSING_COVER_ASSET',
      blocking: true,
      message: translateReportBuilder('validation.missingCoverAsset', {
        lng: language,
      }),
    });
  }

  if (!isOnePager && getSection(config, 'product-description')?.enabled) {
    const weight = Number(product?.weight || 0);
    if (!weight) {
      issues.push({
        id: 'PRODUCT_WEIGHT_MISSING',
        blocking: false,
        message: translateReportBuilder('validation.productWeightMissing', {
          lng: language,
        }),
        sectionId: 'product-description',
      });
    }

    if (!hasMeaningfulValue(product?.functional_unit)) {
      issues.push({
        id: 'PRODUCT_FUNCTIONAL_UNIT_MISSING',
        blocking: false,
        message: translateReportBuilder(
          'validation.productFunctionalUnitMissing',
          { lng: language }
        ),
        sectionId: 'product-description',
      });
    }
  }

  if (
    !isOnePager &&
    getSection(config, 'methodology-standards')?.enabled &&
    product &&
    !hasMethodologyData(product)
  ) {
    issues.push({
      id: 'METHODOLOGY_CONTEXT_MISSING',
      blocking: false,
      message: translateReportBuilder('validation.methodologyContextMissing', {
        lng: language,
      }),
      sectionId: 'methodology-standards',
    });
  }

  if (
    !isOnePager &&
    getSection(config, 'materials-circularity')?.enabled &&
    product &&
    !hasMaterialsCircularityData(product)
  ) {
    issues.push({
      id: 'MATERIALS_CIRCULARITY_MISSING',
      blocking: false,
      message: translateReportBuilder(
        'validation.materialsCircularityMissing',
        { lng: language }
      ),
      sectionId: 'materials-circularity',
    });
  }

  if (
    !isOnePager &&
    getSection(config, 'durability-end-of-life')?.enabled &&
    product &&
    !hasDurabilityData(product)
  ) {
    issues.push({
      id: 'DURABILITY_END_OF_LIFE_MISSING',
      blocking: false,
      message: translateReportBuilder('validation.durabilityEndOfLifeMissing', {
        lng: language,
      }),
      sectionId: 'durability-end-of-life',
    });
  }

  if (
    !isOnePager &&
    (getSection(config, 'results-ghg')?.enabled ||
      getSection(config, 'impact-categories')?.enabled) &&
    !previewData.selectedImpact
  ) {
    issues.push({
      id: 'IMPACT_DATA_MISSING',
      blocking: false,
      message: translateReportBuilder('validation.impactDataMissing', {
        lng: language,
      }),
    });
  }

  const selectedImpactSection = getSection(config, 'impact-categories');
  if (
    !isOnePager &&
    selectedImpactSection?.enabled &&
    Array.isArray(selectedImpactSection.options.selectedAreaIds) &&
    selectedImpactSection.options.selectedAreaIds.length === 0 &&
    selectedImpactSection.options.includeAdditionalEnvironmentalData === false
  ) {
    issues.push({
      id: 'IMPACT_CATEGORY_SELECTION_EMPTY',
      blocking: false,
      message: translateReportBuilder(
        'validation.impactCategorySelectionEmpty',
        {
          lng: language,
        }
      ),
      sectionId: 'impact-categories',
    });
  }

  if (!isOnePager && config.audience === 'audit-ready') {
    const required = AUDIENCE_DEFAULTS['audit-ready'].requiredSections;
    required.forEach((sectionId) => {
      if (!getSection(config, sectionId)?.enabled) {
        issues.push({
          id: `AUDIT_REQUIRED_${sectionId}`,
          blocking: true,
          message: translateReportBuilder('validation.auditRequiredSection', {
            lng: language,
            section: getReportSectionTitle(sectionId, language),
          }),
          sectionId,
        });
      }
    });
  }

  if (config.output.formats.includes('docx')) {
    if (isOnePager) {
      issues.push({
        id: 'ONE_PAGER_DOCX_UNSUPPORTED',
        blocking: true,
        message: translateReportBuilder('validation.onePagerDocxUnsupported', {
          lng: language,
        }),
      });
    }
  }

  return issues;
};

export const hasBlockingIssues = (issues: ReportValidationIssue[]) =>
  issues.some((issue) => issue.blocking);

export const buildAutoInterpretation = (
  previewData: BuilderPreviewData,
  language: ReportConfig['language']
): string => {
  if (!previewData.hotspots.length) {
    return translateReportBuilder('preview.autoInterpretation.noData', {
      lng: language,
    });
  }

  const top = previewData.hotspots
    .map(
      (hotspot) =>
        `${hotspot.stageCode} (${getStageDisplayName(hotspot.stageKey || '', language) || hotspot.name}) ${hotspot.percentage.toFixed(1)}%`
    )
    .join(', ');
  return translateReportBuilder('preview.autoInterpretation.hotspots', {
    lng: language,
    top,
  });
};

export const sha256Hex = async (value: string): Promise<string> => {
  const encoder = new TextEncoder();
  const input = encoder.encode(value);
  const hashBuffer = await crypto.subtle.digest('SHA-256', input);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

const MAX_SVG_LENGTH = 1_000_000;

export const sanitizeSvg = (rawSvg: string): string => {
  if (rawSvg.length > MAX_SVG_LENGTH) {
    throw new Error('SVG file is too large.');
  }

  const hasExternalRef = /(xlink:href|href)\s*=\s*['"]https?:\/\//i.test(
    rawSvg
  );
  if (hasExternalRef) {
    throw new Error('SVG with external references is not allowed.');
  }

  const sanitized = DOMPurify.sanitize(rawSvg, {
    USE_PROFILES: { svg: true },
    FORBID_TAGS: ['script', 'foreignObject'],
    FORBID_ATTR: ['onload', 'onclick', 'onerror'],
  });

  if (!sanitized.includes('<svg')) {
    throw new Error('Invalid SVG file.');
  }

  return sanitized;
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Unable to render image.'));
    image.src = src;
  });

const constrainDimensions = (
  width: number,
  height: number,
  maxSize: number
) => {
  if (width <= maxSize && height <= maxSize) {
    return { width, height };
  }

  const ratio = Math.min(maxSize / width, maxSize / height);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
};

export const svgToPngFile = async (file: File): Promise<File> => {
  const rawSvg = await file.text();
  const sanitizedSvg = sanitizeSvg(rawSvg);
  const svgBlob = new Blob([sanitizedSvg], {
    type: 'image/svg+xml;charset=utf-8',
  });

  const objectUrl = URL.createObjectURL(svgBlob);
  try {
    const image = await loadImage(objectUrl);
    const { width, height } = constrainDimensions(
      image.width,
      image.height,
      1024
    );
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Canvas context is unavailable.');
    }

    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/png', 0.95);
    });

    if (!blob) {
      throw new Error('SVG conversion failed.');
    }

    const outputName = file.name.replace(/\.svg$/i, '.png');
    return new File([blob], outputName, { type: 'image/png' });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

export const resizeImageFile = async (file: File): Promise<File> => {
  const sourceUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(sourceUrl);
    const { width, height } = constrainDimensions(
      image.width,
      image.height,
      1400
    );
    if (
      width === image.width &&
      height === image.height &&
      file.size < 1024 * 1024
    ) {
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Canvas context is unavailable.');
    }

    context.drawImage(image, 0, 0, width, height);

    const outputBlob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/png', 0.9);
    });

    if (!outputBlob) {
      throw new Error('Image processing failed.');
    }

    return new File([outputBlob], file.name.replace(/\.[^/.]+$/, '.png'), {
      type: 'image/png',
    });
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
};

export const sectionHasDataGap = (
  sectionId: SectionId,
  issues: ReportValidationIssue[]
): boolean =>
  issues.some(
    (issue) => issue.sectionId === sectionId && issue.id.includes('MISSING')
  );
